import { reviewWorkflowStepLabel } from "@/components/case-checking/CaseReviewWorkflow";
import { CASE_CHECKING_REVIEW_GRADE_BADGE } from "@/components/case-checking/caseCheckingStatusBadges";
import type {
  DiffItemKind,
  DiffSection,
  GuidedReviewDifferences,
  GuidedReviewDiffSections,
  GuidedReviewRelation,
  GuidedReviewStep,
  ProgressComparison,
} from "@/services/api/case-checking";

export type DiffSectionKey = keyof GuidedReviewDiffSections;

export interface DiffSectionMeta {
  key: DiffSectionKey;
  title: string;
  itemSingular: string;
  itemPlural: string;
}

export const DIFF_SECTION_ORDER: readonly DiffSectionMeta[] = [
  { key: "completeness", title: "Completeness", itemSingular: "completeness decision", itemPlural: "completeness decisions" },
  { key: "checklist", title: "Checklist notes", itemSingular: "checklist note", itemPlural: "checklist notes" },
  { key: "rules", title: "Rule edits", itemSingular: "rule edit", itemPlural: "rule edits" },
  { key: "signoffs", title: "Outcome sign-off", itemSingular: "sign-off", itemPlural: "sign-offs" },
  { key: "remedials", title: "Remedial actions", itemSingular: "remedial edit", itemPlural: "remedial edits" },
] as const;

const CALL_LABELS: Record<string, string> = {
  pass: "Pass",
  fail: "Fail",
  unclear: "Unclear",
  na: "N/A",
  excluded: "Excluded",
  yes: "Yes",
  no: "No",
  agree: "Agree",
  disagree: "Disagree",
  deleted: "Deleted",
  created: "Added by reviewer",
  edited: "Edited",
  suitable: CASE_CHECKING_REVIEW_GRADE_BADGE.suitable.label,
  "dev-needs": CASE_CHECKING_REVIEW_GRADE_BADGE["dev-needs"].label,
  unsuitable: CASE_CHECKING_REVIEW_GRADE_BADGE.unsuitable.label,
};

function joinWithAnd(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function sectionForKey(sections: GuidedReviewDiffSections, key: DiffSectionKey): DiffSection {
  return sections[key];
}

function countItemsByKind(section: DiffSection, kind: DiffItemKind): number {
  return section.items.filter((item) => item.kind === kind).length;
}

function itemNoun(meta: DiffSectionMeta, count: number): string {
  return count === 1 ? meta.itemSingular : meta.itemPlural;
}

function formatSectionCountPhrase(meta: DiffSectionMeta, count: number): string {
  return `${count} ${itemNoun(meta, count)}`;
}

export function countSentence(diff: GuidedReviewDifferences, kind: DiffItemKind): string {
  const phrases: string[] = [];
  for (const meta of DIFF_SECTION_ORDER) {
    const count = countItemsByKind(sectionForKey(diff.sections, meta.key), kind);
    if (count > 0) phrases.push(formatSectionCountPhrase(meta, count));
  }
  return joinWithAnd(phrases);
}

export function changeCounts(diff: GuidedReviewDifferences): {
  added: number;
  removed: number;
  changed: number;
  unchanged: number;
} {
  let added = 0;
  let removed = 0;
  let changed = 0;
  let unchanged = 0;
  for (const meta of DIFF_SECTION_ORDER) {
    const section = sectionForKey(diff.sections, meta.key);
    for (const item of section.items) {
      if (item.kind === "added") added += 1;
      else if (item.kind === "removed") removed += 1;
      else if (item.kind === "changed") changed += 1;
    }
    unchanged += section.unchanged_count;
  }
  return { added, removed, changed, unchanged };
}

function stepsDiffer(progress: ProgressComparison): boolean {
  return progress.selected_step !== progress.current_step;
}

function completedSetsDiffer(progress: ProgressComparison): boolean {
  const a = [...progress.selected_completed].sort().join("\0");
  const b = [...progress.current_completed].sort().join("\0");
  return a !== b;
}

function stepLabel(step: GuidedReviewStep): string {
  return reviewWorkflowStepLabel(step);
}

function returnsToStepClause(progress: ProgressComparison): string {
  if (!stepsDiffer(progress)) return "";
  return `, and returns you to ${stepLabel(progress.selected_step)}`;
}

function movesToStepClause(progress: ProgressComparison): string {
  if (!stepsDiffer(progress)) return "";
  return ` and moves you to ${stepLabel(progress.selected_step)}`;
}

function progressStepMovementClause(progress: ProgressComparison): string {
  if (stepsDiffer(progress)) {
    return `Restoring moves you from ${stepLabel(progress.current_step)} to ${stepLabel(progress.selected_step)}.`;
  }
  if (completedSetsDiffer(progress)) {
    return "Restoring changes which steps are marked complete.";
  }
  return "";
}

function divergedChangeSummary(counts: ReturnType<typeof changeCounts>): string {
  const parts: string[] = [];
  if (counts.added > 0) parts.push(`adds ${counts.added}`);
  if (counts.removed > 0) parts.push(`removes ${counts.removed}`);
  if (counts.changed > 0) parts.push(`changes ${counts.changed}`);
  if (parts.length === 0) return "";
  const total = counts.added + counts.removed + counts.changed;
  const itemWord = total === 1 ? "item" : "items";
  const verbPhrase = joinWithAnd(parts);
  return `Restoring ${verbPhrase} ${itemWord}.`;
}

function divergedDetail(counts: ReturnType<typeof changeCounts>, progress: ProgressComparison): string | undefined {
  const summary = divergedChangeSummary(counts);
  if (!stepsDiffer(progress)) return summary || undefined;
  const stepPart = `Moves you to ${stepLabel(progress.selected_step)}.`;
  if (summary) return `${summary} ${stepPart}`;
  return stepPart;
}

export function callLabel(call: string | null): string {
  if (call == null) return "—";
  return CALL_LABELS[call] ?? call;
}

export function relationBadgeLabel(relation: GuidedReviewRelation): string {
  switch (relation) {
    case "identical":
      return "Same as current";
    case "earlier":
      return "Earlier";
    case "later":
      return "Later";
    case "progress_only":
      return "Progress only";
    case "diverged":
      return "Different";
  }
}

export type VerdictCopy = {
  tone: "neutral" | "info" | "warning";
  title: string;
  detail?: string;
};

export function verdictCopy(diff: GuidedReviewDifferences): VerdictCopy {
  if (diff.is_current_review) {
    return {
      tone: "neutral",
      title: "This is your current review.",
      detail: "Restore is not available.",
    };
  }

  const { progress } = diff;
  const counts = changeCounts(diff);

  switch (diff.relation) {
    case "identical":
      return {
        tone: "neutral",
        title: "Identical to your current review.",
        detail: "Restoring changes nothing.",
      };
    case "earlier": {
      const removedPhrase = countSentence(diff, "removed");
      const detailParts: string[] = [];
      if (removedPhrase) {
        detailParts.push(`Restoring removes ${removedPhrase}${returnsToStepClause(progress)}.`);
      } else if (stepsDiffer(progress)) {
        detailParts.push(`Restoring returns you to ${stepLabel(progress.selected_step)}.`);
      }
      return {
        tone: "warning",
        title: "Earlier version of your current review.",
        detail: detailParts.length > 0 ? detailParts.join(" ") : undefined,
      };
    }
    case "later": {
      const addedPhrase = countSentence(diff, "added");
      let detail = addedPhrase ? `Restoring adds ${addedPhrase}${movesToStepClause(progress)}.` : undefined;
      if (!addedPhrase && stepsDiffer(progress)) {
        detail = `Restoring moves you to ${stepLabel(progress.selected_step)}.`;
      }
      return {
        tone: "info",
        title: "Further along than your current review.",
        detail,
      };
    }
    case "progress_only": {
      const movement = progressStepMovementClause(progress);
      return {
        tone: "info",
        title: "Same answers, different progress.",
        detail: movement || undefined,
      };
    }
    case "diverged": {
      const detail = divergedDetail(counts, progress);
      const tone = counts.removed > 0 || counts.changed > 0 ? "warning" : "info";
      return {
        tone,
        title: "Different from your current review.",
        detail,
      };
    }
  }
}

export function restoreButtonLabel(diff: GuidedReviewDifferences): string {
  const removed = changeCounts(diff).removed;
  if (removed === 0) return "Restore";
  const noun = removed === 1 ? "item" : "items";
  return `Restore (removes ${removed} ${noun})`;
}

function confirmRemovesClause(diff: GuidedReviewDifferences): string {
  const phrase = countSentence(diff, "removed");
  if (!phrase) return "";
  return `This removes ${phrase} from your current review`;
}

function confirmStepClause(progress: ProgressComparison, relation: GuidedReviewRelation): string {
  if (!stepsDiffer(progress)) return "";
  if (relation === "earlier") {
    return ` and returns you to ${stepLabel(progress.selected_step)}`;
  }
  return ` and moves you to ${stepLabel(progress.selected_step)}`;
}

export function restoreConfirmCopy(diff: GuidedReviewDifferences): string {
  if (diff.is_current_review) {
    return "This is your current review; restore is not available.";
  }
  if (diff.relation === "identical") {
    return "This matches your current review; restoring changes nothing.";
  }

  const { progress, relation } = diff;
  const removes = confirmRemovesClause(diff);
  const stepPart = confirmStepClause(progress, relation);
  if (removes) {
    return `${removes}${stepPart}. Your current review stays in Previous reviews.`;
  }
  if (stepsDiffer(progress)) {
    return `This moves you to ${stepLabel(progress.selected_step)}. Your current review stays in Previous reviews.`;
  }
  return "Your current review stays in Previous reviews.";
}
